/*
  <hero-slideshow> — slideshow behaviour for sections/hero-banner.liquid (styles in
  section-hero-banner.css). Only loaded when the hero has more than one slide.

  The slides sit in a CSS scroll-snap track, so swiping works without this script.
  The script adds:
    - previous / next buttons and dots ("Go to slide n", aria-current on the active dot);
    - optional autoplay with a pause / play button (WCAG 2.2.2). Rotation waits while the
      pointer is over the slides, while focus is inside the hero, while the hero is off
      screen or the tab hidden, and in the theme editor. It never starts for visitors who
      prefer reduced motion, and it stops for good once the visitor navigates themselves or
      plays a slide's video with sound;
    - inactive slides are made inert, so keyboard and screen reader users only meet the
      visible slide; the track becomes an aria-live region while rotation is off;
    - the next slide's lazy image is fetched once the page has loaded, so it is ready
      before the slide changes.

  Markup (see the section):
    <hero-slideshow data-autoplay data-speed="6000" data-label-pause="…" data-label-play="…">
      <div data-hero-track> <div class="hero-banner__slide">…</div> … </div>
      <button data-hero-toggle> <button data-hero-prev> <button data-hero-dot="0"> … <button data-hero-next>
    </hero-slideshow>

  Theme editor: selecting a slide block shows that slide and holds rotation until deselected.
*/
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = this.track ? Array.from(this.track.children) : [];
    if (this.slides.length < 2) return;

    this.index = 0;
    this.speed = Math.max(3000, Number(this.dataset.speed) || 6000);
    this.toggleButton = this.querySelector('[data-hero-toggle]');
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));

    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.autoplay = this.hasAttribute('data-autoplay') && !designMode;
    this.paused = !this.autoplay || reducedMotion.matches;
    this.holds = new Set();

    this.abort = new AbortController();
    const { signal } = this.abort;

    this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.goTo(this.index - 1, { user: true }), { signal });
    this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.goTo(this.index + 1, { user: true }), { signal });
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.heroDot), { user: true }), { signal });
    });
    this.toggleButton?.addEventListener('click', () => this.toggle(), { signal });

    // Swipes and trackpad scrolls are the visitor taking over: stop rotating, follow their scroll.
    const takeOver = () => {
      this.arriving = null;
      this.stop();
    };
    this.track.addEventListener('pointerdown', takeOver, { signal });
    this.track.addEventListener('wheel', takeOver, { passive: true, signal });
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });

    // A slide's video played with sound and controls (hero-video.js) is the visitor taking over too.
    this.addEventListener('play', (event) => {
      if (event.target instanceof HTMLVideoElement && event.target.controls) this.stop();
    }, { capture: true, signal });

    // Hovering the controls doesn't hold rotation, so the play button works with a mouse.
    this.track.addEventListener('mouseenter', () => this.hold('hover'), { signal });
    this.track.addEventListener('mouseleave', () => this.release('hover'), { signal });
    this.addEventListener('focusin', (event) => {
      if (event.target !== this.toggleButton) this.hold('focus');
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget) || event.relatedTarget === this.toggleButton) this.release('focus');
    }, { signal });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.hold('hidden');
      else this.release('hidden');
    }, { signal });
    document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event.target), { signal });
    document.addEventListener('shopify:block:deselect', (event) => {
      if (this.contains(event.target)) this.release('editor');
    }, { signal });

    this.intersection = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.release('offscreen');
      else this.hold('offscreen');
    });
    this.intersection.observe(this);

    // Keep the current slide aligned when the width changes.
    this.resizeObserver = new ResizeObserver(() => {
      this.track.scrollLeft = this.slides[this.index].offsetLeft;
    });
    this.resizeObserver.observe(this.track);

    this.render();
    this.schedule();
    this.warmNextSlide();
  }

  disconnectedCallback() {
    if (!this.abort) return;
    this.abort.abort();
    this.intersection.disconnect();
    this.resizeObserver.disconnect();
    clearTimeout(this.timer);
    clearTimeout(this.arrivalTimer);
    cancelAnimationFrame(this.frame);
  }

  goTo(target, { user = false, instant = false } = {}) {
    const count = this.slides.length;
    this.index = ((target % count) + count) % count;
    // The smooth scroll passes the slides in between; onScroll ignores them until it arrives.
    this.arriving = this.index;
    clearTimeout(this.arrivalTimer);
    this.arrivalTimer = setTimeout(() => {
      this.arriving = null;
    }, 1000);
    this.track.scrollTo({
      left: this.slides[this.index].offsetLeft,
      behavior: instant || reducedMotion.matches ? 'auto' : 'smooth',
    });
    if (user) this.paused = true;
    this.render();
    this.schedule();
    this.warmNextSlide();
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      const width = this.track.clientWidth || 1;
      const index = Math.min(this.slides.length - 1, Math.max(0, Math.round(this.track.scrollLeft / width)));
      if (this.arriving != null) {
        if (index === this.arriving) this.arriving = null;
        return;
      }
      if (index === this.index) return;
      this.index = index;
      this.render();
      this.schedule();
      this.warmNextSlide();
    });
  }

  render() {
    this.slides.forEach((slide, n) => {
      const active = n === this.index;
      slide.inert = !active;
      slide.classList.toggle('is-active', active);
      // A slide reached before the page has loaded fetches its image right away.
      if (active) this.loadImages(slide);
    });
    this.dots.forEach((dot, n) => {
      if (n === this.index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    this.toggleAttribute('data-paused', this.paused);
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-label', this.paused ? this.dataset.labelPlay : this.dataset.labelPause);
    }
    this.track.setAttribute('aria-live', this.paused ? 'polite' : 'off');
  }

  schedule() {
    clearTimeout(this.timer);
    if (this.paused || this.holds.size) return;
    this.timer = setTimeout(() => this.goTo(this.index + 1), this.speed);
  }

  hold(reason) {
    this.holds.add(reason);
    clearTimeout(this.timer);
  }

  release(reason) {
    if (!this.holds.delete(reason)) return;
    this.schedule();
  }

  stop() {
    if (this.paused) return;
    this.paused = true;
    this.render();
    clearTimeout(this.timer);
  }

  toggle() {
    this.paused = !this.paused;
    this.render();
    this.schedule();
  }

  onBlockSelect(target) {
    if (!(target instanceof Element) || !this.contains(target)) return;
    const index = this.slides.findIndex((slide) => slide === target || slide.contains(target));
    if (index < 0) return;
    this.hold('editor');
    this.goTo(index, { instant: true });
  }

  loadImages(slide) {
    slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }

  // Fetch the next slide's image after the page has loaded, so a slide change never shows an empty frame.
  warmNextSlide() {
    const run = () => this.loadImages(this.slides[(this.index + 1) % this.slides.length]);
    const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 200));
    if (document.readyState === 'complete') idle(run);
    else window.addEventListener('load', () => idle(run), { once: true, signal: this.abort.signal });
  }
}

if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}
